import { Scale, Sparkles, ShieldCheck, Store, Info, Landmark } from "lucide-react";
import { BUSINESS } from "../data/business";

const PILLARS = [
    {
        icon: Scale,
        title: "IVA Dual: IBS + CBS",
        description: "PIS, COFINS, IPI, ICMS e ISS viram dois impostos: CBS (federal) e IBS (estados e municípios).",
    },
    {
        icon: Sparkles,
        title: "Imposto Seletivo",
        description: "Tributo extra sobre produtos prejudiciais à saúde e ao meio ambiente.",
    },
    {
        icon: Store,
        title: "Simples Nacional continua",
        description: "O Simples segue como opção para MEIs e pequenas empresas.",
    },
];

const TIMELINE = [
    {
        year: "2026",
        title: "Ano de teste",
        detail: "Alíquotas simbólicas (0,9% CBS + 0,1% IBS) passam a constar nas notas.",
    },
    {
        year: "2027",
        title: "CBS substitui PIS e COFINS",
        detail: "Fim do PIS e da COFINS; entram a CBS cheia e o Imposto Seletivo.",
    },
    {
        year: "2029–2032",
        title: "Transição do ICMS e do ISS",
        detail: "Os impostos antigos caem gradualmente enquanto o IBS cresce.",
    },
    {
        year: "2033",
        title: "Novo sistema completo",
        detail: "ICMS, ISS e IPI extintos; novo modelo 100% em vigor.",
    },
];

export const ReformaTributaria = () => {
    return (
        <section id="reforma" data-testid="reforma-section" className="relative py-20 lg:py-32 bg-gradient-to-b from-white via-[#F8FAFC] to-white overflow-hidden">
            <div className="absolute inset-0 dot-grid opacity-40 pointer-events-none" aria-hidden="true" />
            <div className="absolute top-20 -left-40 w-[460px] h-[460px] rounded-full gold-glow animate-drift pointer-events-none" aria-hidden="true" />
            <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="max-w-2xl reveal-on-scroll">
                    <p className="eyebrow text-xs font-bold uppercase tracking-[0.2em] text-blue-900" data-testid="reforma-eyebrow">
                        Reforma Tributária
                    </p>
                    <h1 className="mt-4 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900" data-testid="reforma-title">
                        A maior mudança tributária do país já começou. Sua empresa está pronta?
                    </h1>
                    <p className="mt-4 text-base text-slate-600 leading-relaxed" data-testid="reforma-subtitle">
                        Aprovada pela EC 132/2023 e regulamentada pela LC 214/2025, a reforma simplifica os impostos
                        sobre o consumo — e exige preparação desde já.
                    </p>
                </div>

                <div className="mt-14 grid md:grid-cols-3 gap-6" data-testid="reforma-pillars">
                    {PILLARS.map((pillar, i) => (
                        <div
                            key={pillar.title}
                            data-testid={`reforma-pillar-${i}`}
                            className="group glass-card rounded-2xl p-8 transition-transform duration-300 hover:-translate-y-1 hover:shadow-lg"
                        >
                            <span className="flex items-center justify-center w-12 h-12 rounded-xl bg-slate-900 text-amber-400 mb-6 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3">
                                <pillar.icon className="w-6 h-6" />
                            </span>
                            <h3 className="text-lg font-semibold text-slate-900">{pillar.title}</h3>
                            <p className="mt-3 text-sm text-slate-600 leading-relaxed">{pillar.description}</p>
                        </div>
                    ))}
                </div>

                <div className="mt-16 reveal-on-scroll" data-testid="reforma-timeline">
                    <h3 className="text-xl sm:text-2xl font-medium text-slate-900 flex items-center gap-3">
                        <Landmark className="w-5 h-5 text-blue-900" />
                        Linha do tempo da transição
                    </h3>
                    <div className="mt-10 relative">
                        <div className="absolute left-5 lg:left-0 top-0 bottom-0 lg:top-5 lg:bottom-auto w-0.5 lg:w-full lg:h-0.5 bg-gradient-to-b lg:bg-gradient-to-r from-amber-400 via-amber-300 to-amber-500/40" aria-hidden="true" />
                        <div className="grid gap-10 lg:grid-cols-4 lg:gap-6">
                            {TIMELINE.map((step) => (
                                <div key={step.year} className="relative pl-14 lg:pl-0 lg:pt-14" data-testid={`reforma-step-${step.year.replace(/[^0-9]/g, "")}`}>
                                    <span className="absolute left-0 lg:left-0 top-0 lg:top-0 flex items-center justify-center min-w-10 h-10 px-2.5 rounded-xl bg-slate-900 text-amber-400 text-xs font-bold shadow-md ring-4 ring-white">
                                        {step.year}
                                    </span>
                                    <h4 className="text-base font-semibold text-slate-900">{step.title}</h4>
                                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{step.detail}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="mt-14 flex flex-col sm:flex-row sm:items-center gap-4 justify-between bg-white/70 backdrop-blur-md border border-amber-200 rounded-2xl px-6 py-5 shadow-sm" data-testid="reforma-cta">
                    <p className="flex items-start gap-3 text-sm text-slate-700 leading-relaxed">
                        <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0 text-blue-900" />
                        Quem se prepara agora evita surpresas na apuração, nos preços e no caixa. A WBS acompanha
                        cada fase da transição pela sua empresa.
                    </p>
                    <a
                        href={`${BUSINESS.whatsapp}?text=${encodeURIComponent("Olá! Quero preparar minha empresa para a Reforma Tributária.")}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="reforma-cta-button"
                        className="shrink-0 inline-flex items-center justify-center rounded-lg bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold text-sm px-6 py-3.5 transition-transform duration-200 hover:-translate-y-0.5 shadow-sm hover:shadow-md"
                    >
                        Preparar minha empresa
                    </a>
                </div>

                <p className="mt-6 flex items-start gap-3 text-xs text-slate-500 leading-relaxed" data-testid="reforma-disclaimer">
                    <Info className="w-4 h-4 mt-0.5 shrink-0 text-blue-900" />
                    Resumo educativo com base na EC 132/2023 e na LC 214/2025. Detalhes operacionais seguem em
                    regulamentação — acompanhe a análise do seu caso com o contador.
                </p>
            </div>
        </section>
    );
};
